import { gql } from "@apollo/client";
import { userQueryFieldsType } from "@superloop-bnsc/lib-site-configs";

export const buildUserQuery = (additionalFields?: userQueryFieldsType[]) => {
  const fields = [
    `
      id
      userName
      disabled
      disconnected
      email
      firstName
      addresses {
        edges {
          node {
            id
            streetNo
            address
            suburb
            state
            postCode
            unitNo
            countryId
            country {
              id
              name
            }
          }
        }
      }
      lastName
      deviceLimit
      mobileNumber
      mobileCountry
      unitNumber
      rateProfile {
        id
        name
        description
        profile
      }
      overLimit
      requireAcceptTNC
      requireChangePassword
      role {
        id
        name
      }
      devices {
        edges {
          node {
            id
            macAddress
            description
            disabled
            networkProfile
            clientId
            ip4
            ip6
            leaseExpiry
            hostname
            manufacturer
            isActive
          }
        }
      }
      buildingId
      building {
        id
        name
        code
        formattedAddress
        ewayCountry
      }
      groups {
        edges {
          node {
            id
            name
            active
            code
          }
        }
      }
      activeGroup: getActiveGroup {
        id
        name
        membershipEnd
        membershipStart
      }
      hasUserTOTP

    `
  ];

  if (additionalFields?.includes("accounts")) {
    fields.push(
      `
      accounts {
        edges {
          node {
            id
            name
          }
        }
      }
      `
    )
  }

  if (additionalFields?.includes("getActivePlan")) {
    fields.push(
      `
      activePlan: getActivePlan {
        id
        usage
        unlimited
        total
        start
        end
        product {
          id
          name
          type
          description
          hidden
          once
          value
          rateProfile {
            id
            name
            description
          }
        }
      }
      `
    )
  }

  if (additionalFields?.includes("getActiveOrders")) {
    fields.push(
      `
      activeOrders: getActiveOrders {
        id
        renewalPeriod
        renewsAt
        status
        accountId
        voucherId
        voucher {
          consumed
          availableUsageCount
        }
        orderItems {
          edges {
            node {
              id
              renewable
              productId
              product {
                id
                name
                type
                description
                hidden
                once
                value
                rateProfile {
                  id
                  name
                  description
                  
                }
              }
            }
          }
        }
      }
      `
    )
  }

  if (additionalFields?.includes("conference")) {
    fields.push(
      `
        isConferenceGroup
        activeConferenceGroups: getActiveConferenceGroups {
          id
          name
          code
          active
        }
      `
    )
  }

  return gql`
    query getCurrentUser {
      classMethods {
        User {
          getCurrentUser {
            ${fields.join('\n')}
          }
        }
      }
    }
  `;
}

export const getCurrentUserQuery = gql`
  query getCurrentUser {
    classMethods {
      User {
        getCurrentUser {
          id
          userName
          disabled
          disconnected
          email
          firstName
          addresses {
            edges {
              node {
                id
                streetNo
                address
                suburb
                state
                postCode
                unitNo
                countryId
                country {
                  id
                  name
                }
              }
            }
          }
          lastName
          deviceLimit
          mobileNumber
          mobileCountry
          unitNumber
          rateProfile {
            id
            name
            description
            profile
          }
          overLimit
          requireAcceptTNC
          requireChangePassword
          #accounts {
          #  edges {
          #    node {
          #      id
          #      name
          #    }
          #  }
          #}
          role {
            id
            name
          }
          #activePlan: getActivePlan {
          #  id
          #  usage
          #  unlimited
          #  total
          #  start
          #  end
          #  product {
          #    id
          #    name
          #    type
          #    description
          #    hidden
          #    once
          #    value
          #    rateProfile {
          #      id
          #      name
          #      description
          #    }
          #  }
          #}
          #activeOrders: getActiveOrders {
          #  id
          #  renewalPeriod
          #  renewsAt
          #  status
          #  accountId
          #  voucherId
          #  voucher {
          #    consumed
          #    availableUsageCount
          #  }
          #  orderItems {
          #    edges {
          #      node {
          #        id
          #        renewable
          #        productId
          #        product {
          #          id
          #          name
          #          type
          #          description
          #          hidden
          #          once
          #          value
          #          rateProfile {
          #            id
          #            name
          #            description
          #            
          #          }
          #        }
          #      }
          #    }
          #  }
          #}
          devices {
            edges {
              node {
                id
                macAddress
                description
                disabled
                networkProfile
                clientId
                ip4
                ip6
                leaseExpiry
                hostname
                manufacturer
                isActive
              }
            }
          }
          buildingId
          building {
            id
            name
            code
            formattedAddress
            ewayCountry
          }
          groups {
            edges {
              node {
                id
                name
                active
                code
              }
            }
          }
          activeGroup: getActiveGroup {
            id
            name
            membershipEnd
            membershipStart
          }
          hasUserTOTP
        }
      }
    }
  }
`;

export const createVerificationMutation = gql`
  mutation createVerification (
    $type: verificationType
    $userId: String
    $name: String
    $template: String
  ) {
    classMethods {
      Verification {
        createVerification (
          type: $type
          userId: $userId
          name: $name
          template: $template
        ) {
          id
          valid
        }
      }
    }
  }
`;

export const getAdminGeneratedVerification = gql`
  query getAdminGeneratedVerification ($userId: String) {
    classMethods {
      Verification {
        adminGeneratedVerification(
          userId: $userId
        ) {
          id
          valid
          consumed
        }
      }
    }
  }
`;

export const resendVerificationMutation = gql`
  mutation resendVerification (
    $requestCode: String
  ) {
    classMethods {
      Verification {
        resendVerification (
          requestCode: $requestCode
        )
      }
    }
  }
`;

export const validateVerification = gql`
  query validateVerification (
    $requestCode: String
  ) {
    classMethods {
      Verification {
        validateVerification (
          requestCode: $requestCode
        )
      }
    }
  }
`;

export const logoutMutation = gql`
  mutation {
    classMethods {
      User {
        logout
      }
    }
  }
`;

export const registerUserMutation = gql`
  mutation registerUser(
    $firstName: String!
    $lastName: String!
    $mobileNumber: String
    $mobileCountry: String
    $email: String
    $unitNo: String
    $streetNo: String
    $address: String
    $suburb: String
    $state: String
    $postCode: String
    $countryId: ID
    $building: GQLRegisterUserBuildingInput
    $password: String
    $options: GQLTJson
    $customData: GQLTJson
    $voucherCode: String
    $rateProfileId: ID
    $shouldLogin: Boolean
    $products: [GQLRegisterUserProductsInput]
  ) {
    classMethods {
      User {
        registerUser(
          firstName: $firstName
          lastName: $lastName
          mobileNumber: $mobileNumber
          mobileCountry: $mobileCountry
          email: $email
          unitNo: $unitNo
          streetNo: $streetNo
          address: $address
          suburb: $suburb
          state: $state
          postCode: $postCode
          countryId: $countryId
          building: $building
          password: $password
          options: $options
          customData: $customData
          voucherCode: $voucherCode
          rateProfileId: $rateProfileId
          shouldLogin: $shouldLogin
          products: $products
        ) {
          id
          userName
          jwtToken
          password
          connectionAddress {
            id
          }
        }
      }
    }
  }
`;

export const createAccountMutation = gql`
  mutation createAccount($userId: ID, $ccard: createEwayCustomerCCard, $name: String, $total: Float) {
    classMethods {
      Account {
        createAccount(
          userId: $userId
          ccard: $ccard
          name: $name
          type: EWAY
          ewayCountry: "au",
          total: $total
        ) {
          id
          name
        }
      }
    }
  }
`;

export const updateAccountMutation = gql`
  mutation updateAccount(
    $accountId: ID!,
    $ccard: createEwayCustomerCCard
    $total: Float
    # $ewayCountry: String,
    # $type: GQLAccountType,
    # $accountDefault: Boolean,
    # $customerId: Int,
  ) {
    classMethods {
      Account {
        updateAccount(
          accountId: $accountId,
          ccard: $ccard,
          total: $total
          # ewayCountry: $ewayCountry,
          # type: $type,
          # accountDefault: $accountDefault,
          # customerId: $customerId,
        ) {
          id
        }
      }
    }
  }
`;

export const setPasswordMutation = gql`
  mutation setPassword ($id: ID!, $password: String!, $oldPassword: String!) {
    classMethods {
      UserAuth {
        setPassword (id: $id, password: $password, oldPassword: $oldPassword)
      }
    }
  }
`;

export const updateUserMutation = gql`
  mutation updateUser($userId: ID, $input: UserOptionalInput!) {
    models {
      User(update: {
        where: {
          id: {eq: $userId}
        }
        limit: 1
        input: $input
      }) {
        id
      }
    }
  }
`;

export const generateTotpMutation = gql`
  mutation generateTOTP($id: ID!) {
   classMethods {
    UserAuth {
      generateTOTP(id: $id) {
        otpAuthURL
        secret
        userAuthId
      }
    }
   }
  }
`;

export const removeTotp = gql`
  mutation removeTOTP($id: ID!) {
   classMethods {
    UserAuth {
      removeTOTP(id: $id)
    }
   }
  }
`;